import { useCallback, useEffect, useState } from 'react';
import { useTranscricao } from '../context/TranscricaoContext';
import suggestionsData from '../data/suggestions.json';

/**
 * Hook determinístico para gerenciar ciclo de vida das sugestões.
 * - Exibe até 3 sugestões simultaneamente.
 * - Cada sugestão expira após `duration` segundos.
 * - Sugestões são removidas se a keyword for detectada ou se expirarem.
 */
export default function useSuggestionStateManager() {
  const [completed, setCompleted] = useState(new Set());
  const [active, setActive] = useState(new Map()); // id -> startTime
  const [now, setNow] = useState(Date.now());
  const { latestTranscriptionText } = useTranscricao();

  // Atualiza o tempo continuamente
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Marca sugestões completadas por keyword
  useEffect(() => {
    if (!latestTranscriptionText) return;
    const lowerChunk = String(latestTranscriptionText).toLowerCase();
    setCompleted(prev => {
      const next = new Set(prev);
      // eslint-disable-next-line no-restricted-syntax
      for (const s of suggestionsData) {
        if (s.keywords.some(k => lowerChunk.includes(String(k).toLowerCase()))) {
          next.add(s.id);
        }
      }
      return next;
    });
  }, [latestTranscriptionText]);

  // Atualiza a lista ativa e substitui as expiradas
  useEffect(() => {
    setActive(prev => {
      const next = new Map(prev);
      const newlyExpired = new Set();

      // 1. Remove expiradas e completadas
      // eslint-disable-next-line no-restricted-syntax
      for (const [id, startTime] of next) {
        const s = suggestionsData.find(x => x.id === id);
        // eslint-disable-next-line no-continue
        if (!s) continue;
        const expired = now - startTime > s.duration * 1000;
        if (expired || completed.has(id)) {
          next.delete(id);
          newlyExpired.add(id);
        }
      }

      // 2. Marca expiradas como completadas
      if (newlyExpired.size > 0) {
        setCompleted(prevCompleted => {
          const merged = new Set(prevCompleted);
          newlyExpired.forEach(id => merged.add(id));
          return merged;
        });
      }

      // 3. Pega próximas candidatas (não completadas, não ativas)
      const candidates = suggestionsData
        .filter(s => !completed.has(s.id) && !next.has(s.id))
        .sort((a, b) => a.order - b.order)
        .slice(0, 3 - next.size);

      // 4. Ativa novas
      const nowTs = Date.now();
      candidates.forEach(s => next.set(s.id, nowTs));

      return next;
    });
  }, [completed, now]);

  // Array final de sugestões visíveis
  const activeSuggestions = Array.from(active.keys())
    .map(id => suggestionsData.find(s => s.id === id))
    .filter(Boolean);

  // --- API pública do hook ---
  const completeSuggestion = useCallback(id => {
    setCompleted(prev => new Set(prev).add(id));
  }, []);

  const reset = useCallback(() => {
    setCompleted(new Set());
    setActive(new Map());
  }, []);

  return {
    activeSuggestions,
    completedSuggestions: completed,
    completeSuggestion,
    reset,
  };
}
